import { Compass, Home } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { dialStop } from '@/components/build/types'

interface AdventureDialProps {
  value: number
  onChange: (value: number) => void
  name: string
  regenerating: boolean
}

// The centrepiece control: how far outside the instructor's comfort zone the
// ride should wander. Moving it re-deals every slot you haven't locked.
export function AdventureDial({ value, onChange, name, regenerating }: AdventureDialProps) {
  const stop = dialStop(value)

  return (
    <SpotlightCard className="p-5 sm:p-6">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">How adventurous?</span>
          <div className="relative h-10 overflow-hidden sm:h-12">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={stop.label}
                initial={{ y: 28, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -28, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="text-gradient font-display text-3xl font-semibold leading-tight tracking-tighter sm:text-4xl"
              >
                {stop.label}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <span className={`mb-1 text-xs text-muted-foreground transition-opacity ${regenerating ? 'opacity-100' : 'opacity-0'}`}>re-dealing…</span>
      </div>

      <div className="flex items-center gap-3">
        <Home className="size-5 shrink-0 text-muted-foreground" aria-hidden />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          aria-label="How adventurous the ride should be"
          aria-valuetext={stop.label}
          className="dial-range"
        />
        <Compass className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        {stop.blurb(name)} <span className="opacity-70">Songs you lock stay put.</span>
      </p>
    </SpotlightCard>
  )
}
